// HelpPage.tsx · v12.10.25.3 (#2106 Article exported - the in-app Help drawer shows the same article)
// HelpPage.tsx · v12.10.25 (#2106 the Help landing - /help and Getting started open the search box + Browse all articles + the guides list) · 2026-10-30 · VoiceSpectrum AI
// HelpPage.tsx · v12.10.23.1 (#2102 topics, search and deep links follow the person's rights - useAccess, View is enough) · 2026-10-29 · VoiceSpectrum AI
// HelpPage.tsx · v12.10.11.3 (#1495 one full-width Help bar: the title sits over the left menu on desktop, in the bar on a phone; Close Help back on screen) · 2026-10-09 · VoiceSpectrum AI
// HelpPage.tsx · v12.10.11.2 (#1495 Help top bar; #1498 one clear button) · 2026-10-09 · VoiceSpectrum AI
// v12.10.11.2 (#1495, UI-LOCK-12.10.11.2 v1.0): Tushar 10/9 "it looks like i am still in the app" - a full-width Help top bar
// (book icon, VoiceSpectrum Help, Search help moved up into it, Close Help on the right). Close Help closes the Help tab
// (Help always opens in its own tab, so the app tab is still on the page the person came from); if the browser will not
// close it, it goes to Messages. The Shell paints the left column's top in the same colour (help-mode) so the bar runs
// edge to edge, and captions its menu HELP TOPICS. (#1498) the box is type=search: the browser's own clear button is
// hidden in help.css, so only ours shows.
// T61 #1475 - the Help tab (UI-PROTO-T56 v1.25 screen "help", LOCKED 10/9). Opens in a NEW browser tab from the left
// menu's Help and renders inside the app's own Shell: the same left menu and account card. In the Help tab the left
// menu lists help topics instead of opening screens (Shell reads ?s=). Middle column: Search help… and that menu
// item's topics; right: the article. Deep link /help?a=<article-id> opens one article (?s=<menu item> optional).
// Content: ./helpContent.ts. Screenshots: ./img/<article-id>.png when the file exists, else a grey placeholder.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  HELP_RAILS, findHelpEntry, helpTopicsFor, searchHelp, helpAllowed,
  type HelpCanOpen, type HelpEntry, type HelpRail, type HelpScreenshot,
} from './helpContent';
import './help.css';
import { useAccess } from '../../lib/useAccess';   // v12.10.23.1 (#2102)
import HelpGuides from './HelpGuides';   // v12.10.25 (#2106)

// A screenshot exists when its PNG sits in src/pages/help/img - Vite bundles it at build time (no file = placeholder).
const SHOTS = import.meta.glob('./img/*.png', { eager: true, query: '?url', import: 'default' }) as Record<string, string>;
function shotUrl(file: string): string | undefined {
  const name = file.split('/').pop();
  return name ? SHOTS[`./img/${name}`] : undefined;
}

// v12.10.11.2 (#1495): the book in the Help top bar.
function IconBook() {
  return (
    <svg className="help-top-ic" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  );
}

export function isHelpRail(v: string | null | undefined): v is HelpRail {
  return !!v && HELP_RAILS.some((r) => r.key === v);
}

const railLabel = (r: HelpRail) => HELP_RAILS.find((x) => x.key === r)?.label ?? 'Help';

export default function HelpPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const artRef = useRef<HTMLDivElement>(null);
  const firstRef = useRef(true);

  const sParam = params.get('s');
  const aParam = params.get('a');
  // v12.10.23.1 (#2102): what this person may see - any action on an area (View is enough); while unknown, everything.
  const { me, canOpen } = useAccess();
  const helpCan: HelpCanOpen = useMemo(() => {
    const anySettings = me ? Object.entries(me.access ?? {}).some(([k, v]) => (k.startsWith('s:') || k.startsWith('sec_')) && (v ?? []).length > 0) : true;
    return (k: string) => (k === '*settings' ? anySettings : canOpen(k));
  }, [me, canOpen]);
  const askedAny = findHelpEntry(aParam);
  const asked = askedAny && helpAllowed(askedAny.article.id, helpCan) ? askedAny : undefined;   // a link to a topic the person may not see opens the menu's first topic
  const rail: HelpRail = isHelpRail(sParam) ? sParam : asked?.section.rail ?? 'start';
  const topics = useMemo(() => helpTopicsFor(rail, helpCan), [rail, helpCan]);
  const current: HelpEntry | undefined = asked ?? topics[0]?.entries[0];
  const results = useMemo(() => searchHelp(query, helpCan), [query, helpCan]);
  const searching = query.trim().length > 0;
  // v12.10.25 (#2106): /help and Getting started (no article asked) open the landing - search + Browse all articles + guides.
  const landing = !aParam && (!sParam || sParam === 'start');
  const showLanding = landing && !searching;
  // v12.10.25 (#2106): typing in the landing's search box carries on in the top bar's box (the results list replaces the landing)
  const topSearchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const el = topSearchRef.current;
    if (landing && searching && el && document.activeElement !== el) { el.focus(); const n = el.value.length; try { el.setSelectionRange(n, n); } catch { /* type=search */ } }
  }, [landing, searching]);
  function browseAll() {
    const firstStart = helpTopicsFor('start', helpCan)[0]?.entries[0];
    if (firstStart) setParams({ s: 'start', a: firstStart.article.id });
  }

  // Keep the address canonical (?s=&a=) so the left menu highlights the right item and the tab can be bookmarked.
  useEffect(() => {
    if (!current || landing) return;   // v12.10.25 (#2106): the landing keeps its address
    if (sParam !== rail || aParam !== current.article.id) setParams({ s: rail, a: current.article.id }, { replace: true });
  }, [sParam, aParam, rail, current, setParams, landing]);

  useEffect(() => {
    document.title = current ? `${current.article.title} · Help · VoiceSpectrum` : 'Help · VoiceSpectrum';
    // A new article starts at its top. On a phone the topics sit above the article, so scroll the article into view
    // (not on the first open - the person sees the topics first).
    const narrow = window.matchMedia?.('(max-width: 980px)').matches === true;
    if (narrow && !firstRef.current) artRef.current?.scrollIntoView?.({ block: 'start' });
    else { const main = document.querySelector('.tenant-main'); if (main) main.scrollTop = 0; }
    firstRef.current = false;
  }, [current]);

  // v12.10.11.2 (#1495): Close Help closes this tab (opened from the app's left menu); a tab the browser will not close
  // (Help opened from a typed link) goes to Messages instead.
  function closeHelp() {
    window.close();
    window.setTimeout(() => { if (!window.closed) navigate('/messages'); }, 150);
  }

  function open(e: HelpEntry) {
    const inRail = helpTopicsFor(rail, helpCan).some((g) => g.entries.some((x) => x.article.id === e.article.id));
    setParams({ s: inRail ? rail : e.section.rail, a: e.article.id });
  }

  return (
    <div className="help-shell" data-testid="help-page">
      {/* v12.10.11.2 (#1495): the Help top bar - so nobody mistakes the Help tab for the app. */}
      <header className="help-top">
        <span className="help-top-t"><IconBook /><span>VoiceSpectrum Help</span></span>
        {!showLanding && <div className="help-search help-top-search">
          <input
            ref={topSearchRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search help…"
            aria-label="Search help"
          />
          {searching && <button type="button" className="help-search-x" onClick={() => setQuery('')} aria-label="Clear search">×</button>}
        </div>}
        <button type="button" className="help-close" onClick={closeHelp} title="Close the Help tab - the app is still open in its own tab">
          <span aria-hidden="true">✕</span> Close<span className="help-close-l"> Help</span>
        </button>
      </header>
      {showLanding ? <HelpGuides query={query} onQuery={setQuery} onBrowse={browseAll} /> : (
      <div className="help-page">
      <nav className="help-tree" aria-label="Help topics">
        {searching ? (
          results.length ? (
            <>
              <div className="help-g">{results.length} {results.length === 1 ? 'topic' : 'topics'}</div>
              {results.map((e) => (
                <button key={e.article.id} type="button" className={`help-a${current?.article.id === e.article.id ? ' on' : ''}`} onClick={() => open(e)}>
                  {e.article.title}
                  <small>{e.article.group ?? e.section.title}</small>
                </button>
              ))}
            </>
          ) : (
            <p className="help-none">Nothing matches “{query.trim()}”. Try another word, like “password” or “voicemail”.</p>
          )
        ) : (
          topics.map((g) => (
            <div key={g.heading} className="help-grp">
              <div className="help-g">{g.heading}</div>
              {g.entries.map((e) => (
                <button key={e.article.id} type="button" className={`help-a${current?.article.id === e.article.id ? ' on' : ''}`}
                  aria-current={current?.article.id === e.article.id ? 'page' : undefined} onClick={() => open(e)}>
                  {e.article.title}
                </button>
              ))}
            </div>
          ))
        )}
      </nav>

      <div className="help-main" ref={artRef}>
        {current ? <Article entry={current} rail={rail} onOpenApp={(to) => navigate(to)} /> : <p className="help-none">No help topics here for your user group. Ask your practice's admin if you need this screen.</p>}
      </div>
      </div>
      )}
    </div>
  );
}

export function Article({ entry, rail, onOpenApp }: { entry: HelpEntry; rail: HelpRail; onOpenApp: (to: string) => void }) {
  const { article: a, section: s } = entry;
  const reader = a.reader ?? s.reader;
  const group = a.group ?? s.title;
  return (
    <article className="help-art" aria-labelledby="help-art-title">
      <div className="help-bc">Help › {railLabel(rail)}{group !== railLabel(rail) ? ` › ${group}` : ''}</div>
      <h1 id="help-art-title">{a.title}</h1>
      <p className="help-what">{a.what}</p>
      <div className="help-meta">
        <span className="help-who">For: {reader}</span>
        {a.appLink && (
          <button type="button" className="help-lnk" onClick={() => onOpenApp(a.appLink!)}>
            Open {a.appLabel ?? 'this screen'} in the app →
          </button>
        )}
      </div>

      <h4>Where to find it</h4>
      <div className="help-path">{a.menuPath}</div>

      <h4>How it works</h4>
      <ol className="help-steps">
        {a.steps.map((t, i) => <li key={i}>{t}</li>)}
      </ol>

      {a.fields && a.fields.length > 0 && (
        <>
          <h4>Details</h4>
          <dl className="help-fields">
            {a.fields.map((f) => (
              <div key={f.name} className="help-field">
                <dt>{f.name}</dt>
                <dd>{f.text}</dd>
              </div>
            ))}
          </dl>
        </>
      )}

      <h4>Screenshot</h4>
      <Shot shot={a.screenshot} title={a.title} />

      {a.goodToKnow.length > 0 && (
        <>
          <h4>Good to know</h4>
          {a.goodToKnow.map((t, i) => <div key={i} className="help-gtk">{t}</div>)}
        </>
      )}

      <h4>Who can do it</h4>
      <p className="help-whocan">{a.who}</p>
    </article>
  );
}

function Shot({ shot, title }: { shot: HelpScreenshot; title: string }) {
  const url = shotUrl(shot.file);
  return (
    <figure className="help-shot">
      {url ? (
        <img src={url} alt={`${title} - screenshot with numbered markers`} loading="lazy" />
      ) : (
        <div className="help-shot-ph" role="img" aria-label="Screenshot to follow">
          <span>Screenshot to follow</span>
        </div>
      )}
      {shot.markers.length > 0 && (
        <figcaption>
          <ol className="help-legend">
            {shot.markers.map((m) => (
              <li key={m.n}><span className="help-mk" aria-hidden="true">{m.n}</span>{m.label}</li>
            ))}
          </ol>
        </figcaption>
      )}
    </figure>
  );
}
